/**
 * Company Agent Service
 *
 * Wraps the company-admin agent management API.
 * Endpoints (under role:company_admin|company_owner middleware):
 *   GET  /api/company/agents
 *   GET  /api/company/agents/{userId}
 *   PUT  /api/company/agents/{userId}
 *
 * NO impersonation here by design — agents are external referrers, not employees.
 */

import axiosInstance from '../config/axios.config';

const BASE = '/company/agents';

const companyAgentService = {
    /**
     * Verify the current user is allowed to manage agents.
     * Mirrors companyImpersonationService.isCompanyOwner() — this gate is enforced
     * server-side too, but having it client-side prevents a flicker for non-admins.
     */
    isCompanyAdmin() {
        const userRoles = JSON.parse(localStorage.getItem('userRoles') || '[]');
        return userRoles.includes('company_admin') || userRoles.includes('company_owner');
    },

    /**
     * Fetch a paginated list of agents.
     * @param {Object} params { page, per_page, search, sort_field, sort_order, status }
     */
    async getAgents(params = {}) {
        try {
            const response = await axiosInstance.get(BASE, { params });
            return {
                success: true,
                agents: response.data?.agents || [],
                pagination: response.data?.pagination || null,
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to load agents',
                agents: [],
                pagination: null,
            };
        }
    },

    /**
     * Fetch a single agent's full details (used by the View dialog).
     */
    async getAgent(userId) {
        try {
            const response = await axiosInstance.get(`${BASE}/${userId}`);
            return {
                success: true,
                agent: response.data?.agent || null,
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to load agent',
                status: error.response?.status,
            };
        }
    },

    /**
     * Update agent profile + active status.
     * @param {number} userId
     * @param {Object} payload { first_name, last_name, email, mobile_number, is_active? }
     */
    async updateAgent(userId, payload) {
        try {
            const response = await axiosInstance.put(`${BASE}/${userId}`, payload);
            return {
                success: response.data?.success ?? true,
                agent: response.data?.agent || null,
                message: response.data?.message,
            };
        } catch (error) {
            const data = error.response?.data;
            return {
                success: false,
                message: data?.message || 'Failed to update agent',
                errors: data?.errors || null,
                status: error.response?.status,
            };
        }
    },
};

export default companyAgentService;
